/**
 * When the portal invoice detail query should keep asking the server.
 *
 * Pure and dependency-free so it can be tested without dragging in the portal
 * API client. Two independent reasons to poll:
 *
 *  - A Payabli ACH capture still clearing. The flag is SERVER-derived, so a
 *    reload (or a second device) resumes the poll.
 *  - A just-returned Stripe Checkout payer. Stripe redirects BEFORE its webhook
 *    lands, so the first read after the redirect is the still-unpaid invoice;
 *    without a poll the customer sits on a paid invoice rendering OPEN.
 */
export const PORTAL_ACH_SETTLING_POLL_MS = 4000;
export const PORTAL_STRIPE_SETTLEMENT_POLL_MS = 1500;

/**
 * How long a `?payment=success` return is allowed to mean "settling".
 *
 * The card webhook lands in seconds, so this is generous for the case the
 * param was added for. It exists because the param cannot tell us WHICH rail
 * the payer used, and the portal offers ACH on the Stripe rail too — a
 * `us_bank_account` debit clears in 1-5 BUSINESS DAYS.
 *
 * `paymentAttemptSettling` (server-derived from `SETTLING_CANDIDATE_FILTER`)
 * takes over once a payment is recorded as in flight, and for Stripe that
 * happens only when the webhook writes the PENDING row. Until the webhook lands
 * — or if it never does — nothing server-side ends the `?payment=success` wait,
 * so without this bound the interval below would return 1500 on every tick,
 * with the pay CTAs suppressed the whole time.
 */
export const PORTAL_SETTLEMENT_WINDOW_MS = 120_000;

type PollableInvoice = {
  balanceDue: number;
  paymentAttemptSettling?: boolean;
  latestPaymentStatus?: string | null;
};

/**
 * Statuses that mean the attempt is over and the balance is not coming down.
 * Same set the public invoice page already acts on, so the two pay surfaces
 * agree about what "finished badly" looks like.
 */
const TERMINAL_PAYMENT_STATUSES = new Set([
  'FAILED',
  'DISPUTED',
  'DISPUTE_UNDER_REVIEW',
  'CHARGEBACK_LOST',
]);

export type SettlementWindow = {
  invoice: PollableInvoice | undefined;
  awaitingStripeSettlement: boolean;
  /** 404/403 — the invoice will never load. NOT a transient network failure. */
  hasFatalError: boolean;
  /** Time since the payer landed back on `?payment=success`. */
  elapsedMs: number;
};

/**
 * Is a Stripe payment still plausibly on its way in?
 *
 * ONE predicate for both consumers: it decides whether to keep polling AND
 * whether the page suppresses its pay CTAs. Those must not drift — a page that
 * hides the Pay button after the poll has given up is the stuck state, and a
 * page that offers Pay while still polling is the contradiction this branch
 * set out to remove.
 */
export function portalSettlementPending({
  invoice,
  awaitingStripeSettlement,
  hasFatalError,
  elapsedMs,
}: SettlementWindow): boolean {
  if (!awaitingStripeSettlement) return false;
  // Only a PERMANENT error ends the wait. `retry: false` means a single dropped
  // request errors the query, and treating that as fatal was worse than the bug
  // it fixed: the poll stopped, the param was stripped, and a reload landed on
  // live Pay buttons over money already taken, with nothing left to re-arm from.
  // A transient failure keeps waiting — the window below bounds it anyway.
  if (hasFatalError) return false;
  // The rail is unknowable from the return URL, and a Stripe ACH debit takes
  // days. Without this the wait never ends: see PORTAL_SETTLEMENT_WINDOW_MS.
  if (elapsedMs >= PORTAL_SETTLEMENT_WINDOW_MS) return false;
  // `undefined` means the first response has not arrived yet — still waiting.
  if (!invoice) return true;
  if (invoice.balanceDue <= 0) return false;
  // An async debit can fail AFTER the success redirect. The invoice stays
  // payable, so the balance check above never fires, and the payer would sit
  // on "processing" with no way to retry.
  if (TERMINAL_PAYMENT_STATUSES.has((invoice.latestPaymentStatus ?? '').toUpperCase())) {
    return false;
  }
  return true;
}

export function portalInvoiceRefetchInterval(
  invoice: PollableInvoice | undefined,
  awaitingStripeSettlement: boolean,
  hasFatalError = false,
  elapsedMs = 0,
): number | false {
  if (hasFatalError) {
    return false;
  }
  // Server-derived and legitimately long-running, so it is not subject to the
  // client-side window below.
  if (invoice?.paymentAttemptSettling === true) {
    return PORTAL_ACH_SETTLING_POLL_MS;
  }
  return portalSettlementPending({ invoice, awaitingStripeSettlement, hasFatalError, elapsedMs })
    ? PORTAL_STRIPE_SETTLEMENT_POLL_MS
    : false;
}
